import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, subDays, subMonths } from 'date-fns';
import { useContentProtection } from '../hooks/useContentProtection';
import {
    Activity,
    BookOpen,
    Target,
    Award,
    MessageSquare,
    Briefcase,
    Users,
    FileText,
    CheckCircle,
    XCircle,
    ChevronRight,
    Sparkles,
    AlertCircle,
    Clock,
    TrendingUp,
    GraduationCap,
    Play,
    Eye,
    Loader2,
    Calendar,
    Star,
    ThumbsUp,
    ThumbsDown,
    BarChart3,
    Lightbulb,
    RefreshCw
} from 'lucide-react';

const MySimulations = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    // Enable content protection (disable right-click, copy, screenshots)
    useContentProtection(true);
    
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState('30days');
    
    // Data states
    const [overview, setOverview] = useState(null);
    const [skillProgress, setSkillProgress] = useState([]);
    const [simulationHistory, setSimulationHistory] = useState([]);
    const [feedbackList, setFeedbackList] = useState([]);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const getDateRange = () => {
        const today = new Date();
        switch (dateRange) {
            case '7days': return format(subDays(today, 7), 'yyyy-MM-dd');
            case '30days': return format(subDays(today, 30), 'yyyy-MM-dd');
            case '90days': return format(subDays(today, 90), 'yyyy-MM-dd');
            case '6months': return format(subMonths(today, 6), 'yyyy-MM-dd');
            default: return null;
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [dateRange]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const startDate = getDateRange();
            const params = startDate ? `?start_date=${startDate}` : '';
            
            const [overviewRes, progressRes, historyRes, feedbackRes] = await Promise.all([
                api.get(`/my-simulations/overview${params}`),
                api.get('/job-simulations/progress'),
                api.get(`/my-simulations/history${params}`),
                api.get(`/my-simulations/feedback${params}`)
            ]);
            
            setOverview(overviewRes.data);
            setSkillProgress(progressRes.data.skills || []);
            setSimulationHistory(historyRes.data.simulations || []);
            setFeedbackList(feedbackRes.data.feedback || []);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load your simulation data');
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-amber-500';
        return 'text-red-500';
    };

    const getScoreBg = (score) => {
        if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/30';
        if (score >= 60) return 'bg-amber-500/10 border-amber-500/30';
        return 'bg-red-500/10 border-red-500/30';
    };

    const getScenarioIcon = (type) => {
        switch (type) {
            case 'communication': return <MessageSquare className="h-4 w-4" />;
            case 'job_analysis': return <FileText className="h-4 w-4" />;
            case 'candidate_screening': return <Users className="h-4 w-4" />;
            case 'client_intake': return <Briefcase className="h-4 w-4" />;
            default: return <Target className="h-4 w-4" />;
        }
    };

    const viewFeedbackDetails = (feedback) => {
        setSelectedFeedback(feedback);
        setShowFeedbackModal(true);
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="my-simulations-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Activity className="h-8 w-8 text-primary" />
                            My Simulations
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your learning progress, review feedback, and plan improvements
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <Select value={dateRange} onValueChange={setDateRange}>
                            <SelectTrigger className="w-[160px]">
                                <Calendar className="h-4 w-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7days">Last 7 days</SelectItem>
                                <SelectItem value="30days">Last 30 days</SelectItem>
                                <SelectItem value="90days">Last 90 days</SelectItem>
                                <SelectItem value="6months">Last 6 months</SelectItem>
                                <SelectItem value="all">All time</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        <Button variant="outline" size="icon" onClick={fetchAllData} data-testid="refresh-btn">
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Skills Learned</p>
                                    <p className="text-2xl font-bold text-primary">{overview?.skills_completed || 0}</p>
                                </div>
                                <GraduationCap className="h-8 w-8 text-primary/30" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Simulations</p>
                                    <p className="text-2xl font-bold">{overview?.total_simulations || 0}</p>
                                </div>
                                <Target className="h-8 w-8 text-muted-foreground/30" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Completed</p>
                                    <p className="text-2xl font-bold text-emerald-500">{overview?.completed || 0}</p>
                                </div>
                                <CheckCircle className="h-8 w-8 text-emerald-500/30" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Avg Score</p>
                                    <p className={`text-2xl font-bold ${getScoreColor(overview?.average_score || 0)}`}>
                                        {overview?.average_score || 0}%
                                    </p>
                                </div>
                                <Award className="h-8 w-8 text-amber-500/30" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Study Hours</p>
                                    <p className="text-2xl font-bold">{overview?.total_hours || 0}h</p>
                                </div>
                                <Clock className="h-8 w-8 text-blue-500/30" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="skills" data-testid="tab-skills">Job Skills</TabsTrigger>
                        <TabsTrigger value="history" data-testid="tab-history">Simulation History</TabsTrigger>
                        <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback Review</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6 mt-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Recent Activity */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Activity className="h-5 w-5 text-primary" />
                                        Recent Activity
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[300px]">
                                        <div className="space-y-3">
                                            {simulationHistory.slice(0, 5).map((sim, idx) => (
                                                <div 
                                                    key={sim.id || idx} 
                                                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className={`p-2 rounded-lg ${getScoreBg(sim.overall_score)}`}>
                                                        {getScenarioIcon(sim.scenario_type)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate capitalize">
                                                            {sim.scenario_type?.replace('_', ' ') || 'Simulation'}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">{sim.date}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`font-bold ${getScoreColor(sim.overall_score)}`}>
                                                            {sim.overall_score}%
                                                        </p>
                                                        <Badge variant={sim.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                                            {sim.status}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                            {simulationHistory.length === 0 && (
                                                <div className="text-center py-8 text-muted-foreground">
                                                    <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                                    <p>No simulations yet</p>
                                                    <Button 
                                                        variant="link" 
                                                        className="mt-2"
                                                        onClick={() => navigate('/simulations')}
                                                    >
                                                        Start your first simulation
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>

                            {/* Skills Progress */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <BookOpen className="h-5 w-5 text-primary" />
                                        Skills in Progress
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[300px]">
                                        <div className="space-y-3">
                                            {skillProgress.filter(s => s.overall_status === 'in_progress').slice(0, 5).map((skill, idx) => (
                                                <div 
                                                    key={skill.skill_id || idx}
                                                    className="p-3 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer"
                                                    onClick={() => navigate(`/skill-learning/${skill.skill_id}`)}
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <p className="font-medium text-sm">{skill.skill_name || skill.skill_id}</p>
                                                        <Badge variant="outline" className="text-xs">
                                                            {skill.lessons_completed || 0}/{skill.total_lessons || 5} lessons
                                                        </Badge>
                                                    </div>
                                                    <Progress 
                                                        value={((skill.lessons_completed || 0) / (skill.total_lessons || 5)) * 100} 
                                                        className="h-2"
                                                    />
                                                </div>
                                            ))}
                                            {skillProgress.filter(s => s.overall_status === 'in_progress').length === 0 && (
                                                <div className="text-center py-8 text-muted-foreground">
                                                    <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                                    <p>No skills in progress</p>
                                                    <Button 
                                                        variant="link" 
                                                        className="mt-2"
                                                        onClick={() => navigate('/job-simulations')}
                                                    >
                                                        Browse Job Skills
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Performance Insights */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Lightbulb className="h-5 w-5 text-primary" />
                                    Performance Insights
                                </CardTitle>
                                <CardDescription>
                                    Key areas based on your recent simulation performance
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                                        <h4 className="font-medium text-emerald-600 flex items-center gap-2 mb-3">
                                            <ThumbsUp className="h-4 w-4" />
                                            Your Strengths
                                        </h4>
                                        <div className="space-y-2">
                                            {overview?.strengths?.length > 0 ? overview.strengths.map((s, i) => (
                                                <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                                    <Sparkles className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                    {s}
                                                </p>
                                            )) : (
                                                <p className="text-sm text-muted-foreground">Complete more simulations to see your strengths</p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                                        <h4 className="font-medium text-amber-600 flex items-center gap-2 mb-3">
                                            <TrendingUp className="h-4 w-4" />
                                            Areas to Improve
                                        </h4>
                                        <div className="space-y-2">
                                            {overview?.improvements?.length > 0 ? overview.improvements.map((s, i) => (
                                                <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                                    <ChevronRight className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                                    {s}
                                                </p>
                                            )) : (
                                                <p className="text-sm text-muted-foreground">Complete more simulations to see improvement areas</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Skills Tab */}
                    <TabsContent value="skills" className="mt-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-lg">Job Skills Progress</CardTitle>
                                        <CardDescription>
                                            Your learning journey across different job skills
                                        </CardDescription>
                                    </div>
                                    <Button onClick={() => navigate('/job-simulations')} data-testid="browse-skills-btn">
                                        <BookOpen className="h-4 w-4 mr-2" />
                                        Browse All Skills
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {skillProgress.length > 0 ? (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {skillProgress.map((skill, idx) => {
                                            const progress = ((skill.lessons_completed || 0) / (skill.total_lessons || 5)) * 100;
                                            const isCompleted = skill.overall_status === 'completed';
                                            return (
                                                <div 
                                                    key={skill.skill_id || idx}
                                                    onClick={() => navigate(`/skill-learning/${skill.skill_id}`)}
                                                    className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
                                                        isCompleted 
                                                            ? 'bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50' 
                                                            : 'hover:border-primary/50'
                                                    }`}
                                                    data-testid={`skill-progress-${skill.skill_id}`}
                                                >
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex items-center gap-2">
                                                            {isCompleted ? (
                                                                <CheckCircle className="h-5 w-5 text-emerald-500" />
                                                            ) : (
                                                                <Play className="h-5 w-5 text-primary" />
                                                            )}
                                                            <p className="font-medium text-sm line-clamp-1">
                                                                {skill.skill_name || skill.skill_id}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    <Progress value={progress} className="h-2 mb-2" />
                                                    
                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                        <span>{skill.lessons_completed || 0} of {skill.total_lessons || 5} lessons</span>
                                                        <span>{skill.simulations_completed || 0} simulations</span>
                                                    </div>
                                                    
                                                    {skill.best_score > 0 && (
                                                        <div className="mt-2 flex items-center gap-1 text-xs">
                                                            <Star className="h-3 w-3 text-amber-500" />
                                                            <span>Best: {skill.best_score}%</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <GraduationCap className="h-16 w-16 mx-auto mb-4 opacity-30" />
                                        <h3 className="text-lg font-medium mb-2">No skills started yet</h3>
                                        <p className="mb-4">Start learning job skills to track your progress here</p>
                                        <Button onClick={() => navigate('/job-simulations')}>
                                            Explore Job Skills
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value="history" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Simulation History</CardTitle>
                                <CardDescription>
                                    Complete history of all your simulation sessions
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y">
                                    {simulationHistory.length > 0 ? simulationHistory.map((sim, idx) => (
                                        <div 
                                            key={sim.id || idx}
                                            className="p-4 hover:bg-muted/30 transition-colors"
                                            data-testid={`simulation-history-${idx}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-xl ${getScoreBg(sim.overall_score)}`}>
                                                    {getScenarioIcon(sim.scenario_type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <p className="font-medium capitalize">
                                                            {sim.scenario_type?.replace('_', ' ') || 'Simulation'}
                                                        </p>
                                                        <Badge variant={sim.status === 'completed' ? 'default' : 'secondary'}>
                                                            {sim.status}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground line-clamp-1">
                                                        {sim.topic || 'General practice session'}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="h-3 w-3" />
                                                            {sim.date}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {sim.duration_minutes || 0} min
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <MessageSquare className="h-3 w-3" />
                                                            {sim.exchanges || 0} exchanges
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`text-2xl font-bold ${getScoreColor(sim.overall_score)}`}>
                                                        {sim.overall_score}%
                                                    </p>
                                                    {sim.has_feedback && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            className="text-xs"
                                                            onClick={() => viewFeedbackDetails(sim)}
                                                        >
                                                            <Eye className="h-3 w-3 mr-1" />
                                                            View Feedback
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-12 text-muted-foreground">
                                            <Target className="h-16 w-16 mx-auto mb-4 opacity-30" />
                                            <h3 className="text-lg font-medium mb-2">No simulations yet</h3>
                                            <p className="mb-4">Start practicing to see your history here</p>
                                            <Button onClick={() => navigate('/simulations')}>
                                                Start Simulation
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Feedback Tab */}
                    <TabsContent value="feedback" className="mt-6">
                        <div className="space-y-4">
                            {feedbackList.length > 0 ? feedbackList.map((fb, idx) => (
                                <Card key={idx} className="hover:shadow-md transition-shadow">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${getScoreBg(fb.score)}`}>
                                                    {getScenarioIcon(fb.type)}
                                                </div>
                                                <div>
                                                    <CardTitle className="text-base capitalize">
                                                        {fb.type?.replace('_', ' ') || 'Simulation'}
                                                    </CardTitle>
                                                    <CardDescription>{fb.date}</CardDescription>
                                                </div>
                                            </div>
                                            <Badge className={`${getScoreBg(fb.score)} text-sm`}>
                                                Score: {fb.score}%
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {fb.overall_feedback && (
                                            <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                                                {fb.overall_feedback}
                                            </p>
                                        )}
                                        
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                                <h4 className="text-sm font-medium text-emerald-600 flex items-center gap-2 mb-2">
                                                    <ThumbsUp className="h-4 w-4" />
                                                    What you did well
                                                </h4>
                                                <ul className="space-y-1">
                                                    {fb.strengths?.map((s, i) => (
                                                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                                            <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                                                            {s}
                                                        </li>
                                                    )) || <li className="text-xs text-muted-foreground">No specific strengths recorded</li>}
                                                </ul>
                                            </div>
                                            
                                            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                                <h4 className="text-sm font-medium text-amber-600 flex items-center gap-2 mb-2">
                                                    <TrendingUp className="h-4 w-4" />
                                                    Areas to improve
                                                </h4>
                                                <ul className="space-y-1">
                                                    {fb.improvements?.map((s, i) => (
                                                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                                            <ChevronRight className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                                                            {s}
                                                        </li>
                                                    )) || <li className="text-xs text-muted-foreground">No specific improvements recorded</li>}
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        {/* Skill Scores */}
                                        {fb.skill_scores && Object.keys(fb.skill_scores).length > 0 && (
                                            <div className="pt-3 border-t">
                                                <h4 className="text-sm font-medium mb-3">Skill Breakdown</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    {Object.entries(fb.skill_scores).map(([skill, score]) => (
                                                        <div key={skill} className="text-center p-2 rounded-lg bg-muted/30">
                                                            <p className={`text-lg font-bold ${getScoreColor(score)}`}>{score}%</p>
                                                            <p className="text-xs text-muted-foreground capitalize">{skill.replace('_', ' ')}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )) : (
                                <Card>
                                    <CardContent className="text-center py-12 text-muted-foreground">
                                        <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                                        <h3 className="text-lg font-medium mb-2">No feedback available</h3>
                                        <p className="mb-4">Complete simulations to receive detailed feedback</p>
                                        <Button onClick={() => navigate('/simulations')}>
                                            Start Simulation
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Feedback Detail Modal */}
            <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedFeedback && getScenarioIcon(selectedFeedback.scenario_type)}
                            <span className="capitalize">
                                {selectedFeedback?.scenario_type?.replace('_', ' ')} Feedback
                            </span>
                        </DialogTitle>
                        <DialogDescription>
                            {selectedFeedback?.date} • Score: {selectedFeedback?.overall_score}%
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedFeedback && (
                        <div className="space-y-4">
                            <div className="p-4 rounded-xl bg-muted/30">
                                <h4 className="font-medium mb-2">Overall Feedback</h4>
                                <p className="text-sm text-muted-foreground">
                                    {selectedFeedback.feedback_summary || 'No detailed feedback available.'}
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                {selectedFeedback.scores && Object.entries(selectedFeedback.scores).map(([key, value]) => (
                                    key !== 'overall' && typeof value === 'number' && (
                                        <div key={key} className="p-3 rounded-lg border">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                                                <span className={`font-bold ${getScoreColor(value)}`}>{value}%</span>
                                            </div>
                                            <Progress value={value} className="h-2" />
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </MainLayout>
    );
};

export default MySimulations;
